import {Question} from './question';

export type Exam = {
    id?: string,
    name?: ExamName,
    version?: ExamVersion,
    language?: 'fr' | 'en',
    type?: ExamType,
    img?: string,
    /** Whether the exam is offered to learners. */
    available?: boolean
}

export type ScoreRange = {
    min: number;
    max: number;
}

export type LanguageTier = {
    id: string;                 // e.g. "beginner", "advanced"
    label: string;              // Human-readable name
    description: string;

    // Optional mappings depending on exam
    band?: ScoreRange;          // IELTS
    score?: ScoreRange;         // TOEFL
    points?: ScoreRange;        // TCF
    cefr?: ("A1" | "A2" | "B1" | "B2" | "C1" | "C2")[];
    clb?: ScoreRange;           // TCF Canada
}

export type LanguageTest = {
    exam: ExamType
    scale: string
    tiers: LanguageTier[]
}

export type LanguageTestConfig = {
    [key in ExamType]?: LanguageTest
}

export type ExamName = 'IELTS' | 'TOEFL' | 'TCF'
export type ExamType = "ielts" | "toefl" | "tcf_ca" | "tcf_fr" | "celpip" | "general"
export type ExamScoreName = "band" | "score" | "points" | "cefr" | "clb"
export type ExamVersion = 'academic' | 'general'

export const EXAM_TYPE_MAPPER = [{
    value: 'ielts',
    language: 'en',
    label: 'IELTS',
    description: "International English Language Testing System",
    available: true
}, {
    value: 'tcf_ca',
    language: 'fr',
    label: 'TCF (Canada)',
    description: "Test de Connaissance du Français",
    available: false
},
    {
        value: 'tcf_fr',
        language: 'fr',
        label: 'TCF (France)',
        description: "Test de Connaissance du Français",
        available: false
    }, {
        value: 'toefl',
        language: 'en',
        label: 'TOEFL',
        description: "Test Of English as Foreign Language",
        available: false
    }]
export const EXAM_LANGUAGES = ['en', 'fr']

export const LANGUAGE_TIERS: LanguageTestConfig = {
    ielts: {
        exam: "ielts",
        scale: "Band 0–9",
        tiers: [
            {
                id: "beginner",
                label: "beginnerTierLabel",
                band: {min: 0, max: 3.5},
                description: "ieltsBeginnerTierDescription"
            },
            {
                id: "elementary",
                label: "elementaryTierLabel",
                band: {min: 4.0, max: 4.5},
                description: "ieltsElementaryTierDescription"
            },
            {
                id: "intermediate",
                label: "intermediateTierLabel",
                band: {min: 5.0, max: 5.5},
                description: "ieltsIntermediateTierDescription"
            },
            {
                id: "upper_intermediate",
                label: "upperIntermediateTierLabel",
                band: {min: 6.0, max: 6.5},
                description: "ieltsUpperIntermediateTierDescription"
            },
            {
                id: "advanced",
                label: "advancedTierLabel",
                band: {min: 7.0, max: 7.5},
                description: "ieltsAdvancedTierDescription"
            },
            {
                id: "proficient",
                label: "proficientTierLabel",
                band: {min: 8.0, max: 9.0},
                description: "ieltsProficientTierDescription"
            }
        ]
    },

    toefl: {
        exam: "toefl",
        scale: "Score 0–120",
        tiers: [
            {
                id: "beginner",
                label: "beginnerTierLabel",
                score: {min: 0, max: 40},
                description: "toeflBeginnerTierDescription"
            },
            {
                id: "elementary",
                label: "elementaryTierLabel",
                score: {min: 41, max: 60},
                description: "toeflElementaryTierDescription"
            },
            {
                id: "intermediate",
                label: "intermediateTierLabel",
                score: {min: 61, max: 80},
                description: "toeflIntermediateTierDescription"
            },
            {
                id: "upper_intermediate",
                label: "upperIntermediateTierLabel",
                score: {min: 81, max: 95},
                description: "toeflUpperIntermediateTierDescription"
            },
            {
                id: "advanced",
                label: "advancedTierLabel",
                score: {min: 96, max: 109},
                description: "toeflAdvancedTierDescription"
            },
            {
                id: "proficient",
                label: "proficientTierLabel",
                score: {min: 110, max: 120},
                description: "toeflProficientTierDescription"
            }
        ]
    },

    tcf_fr: {
        exam: "tcf_fr",
        scale: "CEFR / Points",
        tiers: [
            {
                id: "beginner",
                label: "beginnerTierLabel",
                cefr: ["A1"],
                points: {min: 100, max: 199},
                description: "tcfFRBeginnerTierDescription"
            },
            {
                id: "elementary",
                label: "elementaryTierLabel",
                cefr: ["A2"],
                points: {min: 200, max: 299},
                description: "tcfFRElementaryTierDescription"
            },
            {
                id: "intermediate",
                label: "intermediateTierLabel",
                cefr: ["B1"],
                points: {min: 300, max: 399},
                description: "tcfFRIntermediateTierDescription"
            },
            {
                id: "upper_intermediate",
                label: "upperIntermediateTierLabel",
                cefr: ["B2"],
                points: {min: 400, max: 499},
                description: "tcfFRUpperIntermediateTierDescription"
            },
            {
                id: "advanced",
                label: "advancedTierLabel",
                cefr: ["C1"],
                points: {min: 500, max: 599},
                description: "tcfFRAdvancedTierDescription"
            },
            {
                id: "proficient",
                label: "proficientTierLabel",
                cefr: ["C2"],
                points: {min: 600, max: 699},
                description: "tcfFRProficientTierDescription"
            }
        ]
    },

    tcf_ca: {
        exam: "tcf_ca",
        scale: "CLB / NCLC",
        tiers: [
            {
                id: "beginner",
                label: "beginnerTierLabel",
                clb: {min: 1, max: 4},
                description: "tcfCABeginnerTierDescription"
            },
            {
                id: "elementary",
                label: "elementaryTierLabel",
                clb: {min: 5, max: 6},
                description: "tcfCAElementaryTierDescription"
            },
            {
                id: "intermediate",
                label: "intermediateTierLabel",
                clb: {min: 7, max: 7},
                description: "tcfCAIntermediateTierDescription"
            },
            {
                id: "upper_intermediate",
                label: "upperIntermediateTierLabel",
                clb: {min: 8, max: 8},
                description: "tcfCAUpperIntermediateTierDescription"
            },
            {
                id: "advanced",
                label: "advancedTierLabel",
                clb: {min: 9, max: 9},
                description: "tcfCAAdvancedTierDescription"
            },
            {
                id: "proficient",
                label: "proficientTierLabel",
                clb: {min: 10, max: 12},
                description: "tcfCAProficientTierDescription"
            }
        ]
    }
}

/** The range field each exam reports its overall score in. */
export const EXAM_SCORE_FIELD: { [key in ExamType]?: ExamScoreName } = {
    ielts: "band",
    toefl: "score",
    tcf_fr: "points",
    tcf_ca: "clb",
}

/**
 * Find the band a session's overall score falls into, so the result screen can
 * name it with the same localized title and description the tier picker uses.
 *
 * Returns undefined when the exam is unknown or the score sits outside every
 * band — callers render their own fallback rather than an empty card.
 */
export const resolveLanguageTier = (examType?: ExamType, score?: number): LanguageTier | undefined => {
    const test = LANGUAGE_TIERS[examType]

    if (!test || score === undefined || score === null)
        return undefined

    const inRange = (tier: LanguageTier, field: ExamScoreName) => {
        const range = tier[field] as ScoreRange | undefined
        return !!range && score >= range.min && score <= range.max
    }

    const direct = test.tiers.find(tier => inRange(tier, EXAM_SCORE_FIELD[examType]))

    if (direct)
        return direct

    /*
     * TCF Canada bands are expressed in CLB, but a session reports the TCF
     * points score the two TCF exams share. Pick the band on the France points
     * scale and return the Canada tier carrying the same id, so the learner
     * still gets the immigration-oriented wording.
     */
    if (examType === "tcf_ca") {
        const byPoints = LANGUAGE_TIERS.tcf_fr?.tiers.find(tier => inRange(tier, "points"))
        return byPoints && test.tiers.find(tier => tier.id === byPoints.id)
    }

    return undefined
}

export const EXAM_DURATION_MAPPER: { [key in ExamType]?: number } = {
    ielts: 9900,
    toefl: 10800
}


export const SUPPORTED_EXAMS = [{
    "_id": "22c9349c-ceea-4a55-a630-c91864496b1d",
    "img": "https://storage.googleapis.com/linguascore-c018a.firebasestorage.app/%2Fimages%2F%2Fexams%2Fthumb_22c9349c-ceea-4a55-a630-c91864496b1d.png",
    "name": "IELTS",
    "type": "ielts",
    "version": "general",
    "language": "en",
    "id": "22c9349c-ceea-4a55-a630-c91864496b1d",
    "createdAt": 1774002546200
},
    {
        "_id": "d5e7487c-5be5-4049-b410-3e2c95831719",
        "img": "https://storage.googleapis.com/linguascore-c018a.firebasestorage.app/%2Fimages%2F%2Fexams%2Fthumb_d5e7487c-5be5-4049-b410-3e2c95831719.png",
        "name": "TCF",
        "type": "tcf_ca",
        "language": "fr",
        "version": "general",
        "id": "d5e7487c-5be5-4049-b410-3e2c95831719",
        "createdAt": 1774002546225
    },
    {
        "_id": "ff7505f2-83e3-463b-8cc5-a6be2ec67635",
        "img": "https://storage.googleapis.com/linguascore-c018a.firebasestorage.app/%2Fimages%2F%2Fexams%2Fthumb_ff7505f2-83e3-463b-8cc5-a6be2ec67635.png",
        "name": "TCF",
        "type": "tcf_fr",
        "language": "fr",
        "version": "general",
        "id": "ff7505f2-83e3-463b-8cc5-a6be2ec67635",
        "createdAt": 1774002546227
    }]
